import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthContext'
import { listProposals, type ProposalRecord } from '../api/proposals'
import { navigate } from '../App'
import { colors } from '../theme'
import StatusPill from './../components/StatusPill'
import UploadProposalModal from './../components/UploadProposalModal'

const PROPOSALS_GRID_COLUMNS = '1.4fr 1fr 1fr 0.8fr 0.8fr 0.6fr'

export default function Proposals() {
  const { authHeaders, apiBase, user } = useAuth()
  const [items, setItems] = useState<ProposalRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [showUpload, setShowUpload] = useState(false)

  useEffect(() => {
    let cancelled = false
    setItems(null)
    setError(null)
    listProposals(apiBase, authHeaders(), { mine: !showAll })
      .then((rows) => {
        if (!cancelled) setItems(rows)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load')
      })
    return () => {
      cancelled = true
    }
  }, [apiBase, authHeaders, showAll])

  return (
    <div>
      <SectionHeader
        eyebrow={showAll ? 'All Parkify Proposals' : `Sent by ${user?.email}`}
        title="Proposals"
        actions={
          <>
            <button
              onClick={() => setShowAll((v) => !v)}
              style={{
                background: colors.white,
                border: `1px solid ${colors.divider}`,
                color: colors.black,
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}
            >
              {showAll ? 'Show mine only' : 'Show all reps'}
            </button>
            <button
              onClick={() => setShowUpload(true)}
              style={{
                background: colors.white,
                border: `1px solid ${colors.divider}`,
                color: colors.black,
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
              }}
            >
              Upload PDF
            </button>
            <button
              onClick={() => navigate({ name: 'new' })}
              style={{
                background: colors.purple,
                color: colors.white,
                border: 'none',
                borderRadius: 8,
                padding: '8px 16px',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '1.2px',
                textTransform: 'uppercase',
              }}
            >
              + New proposal
            </button>
          </>
        }
      />

      {error ? (
        <ErrorBlock message={error} />
      ) : items === null ? (
        <SkeletonRows />
      ) : items.length === 0 ? (
        <EmptyState onNew={() => navigate({ name: 'new' })} />
      ) : (
        <Table rows={items} />
      )}

      {showUpload && (
        <UploadProposalModal
          onClose={() => setShowUpload(false)}
          onUploaded={(id) => {
            setShowUpload(false)
            // Straight to the detail screen — the next thing a rep wants after
            // uploading is the share link, which lives there.
            navigate({ name: 'detail', id })
          }}
        />
      )}
    </div>
  )
}

function SectionHeader({
  eyebrow,
  title,
  actions,
}: {
  eyebrow: string
  title: string
  actions?: React.ReactNode
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        gap: 16,
        marginBottom: 24,
        flexWrap: 'wrap',
      }}
    >
      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '2.5px',
            color: colors.purple,
            textTransform: 'uppercase',
            marginBottom: 6,
          }}
        >
          {eyebrow}
        </div>
        <h1
          style={{
            fontSize: 32,
            fontWeight: 700,
            letterSpacing: '-1px',
            margin: 0,
            color: colors.black,
          }}
        >
          {title}
        </h1>
      </div>
      {actions ? (
        <div style={{ display: 'flex', gap: 8 }}>{actions}</div>
      ) : null}
    </div>
  )
}

function Table({ rows }: { rows: ProposalRecord[] }) {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        overflowX: 'auto',
        boxShadow: '0 2px 8px rgba(35,31,32,0.04)',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: PROPOSALS_GRID_COLUMNS,
          minWidth: 760,
          padding: '14px 20px',
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '2px',
          textTransform: 'uppercase',
          color: colors.purple,
          borderBottom: `1px solid ${colors.divider}`,
          background: colors.bgAlt,
        }}
      >
        <div>Company / Prospect</div>
        <div>City</div>
        <div>Rep</div>
        <div>Status</div>
        <div>Created</div>
        <div></div>
      </div>
      {rows.map((r, idx) => (
        <button
          key={r.id}
          onClick={() => navigate({ name: 'detail', id: r.id })}
          style={{
            display: 'grid',
            gridTemplateColumns: PROPOSALS_GRID_COLUMNS,
            minWidth: 760,
            width: '100%',
            padding: '16px 20px',
            background: colors.white,
            border: 'none',
            borderBottom:
              idx === rows.length - 1 ? 'none' : `1px solid ${colors.divider}`,
            textAlign: 'left',
            color: colors.black,
            cursor: 'pointer',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => {
            ;(e.currentTarget as HTMLElement).style.background = colors.purpleSoft
          }}
          onMouseLeave={(e) => {
            ;(e.currentTarget as HTMLElement).style.background = colors.white
          }}
        >
          <div>
            <div style={{ fontWeight: 700, color: colors.black }}>
              {r.company}
            </div>
            <div style={{ fontSize: 13, color: colors.textMuted, marginTop: 2 }}>
              {r.prospect}
            </div>
          </div>
          <div style={{ color: colors.textMuted, fontSize: 13 }}>{r.city}</div>
          <div style={{ color: colors.textMuted, fontSize: 13 }}>
            {r.rep_name ?? r.rep_email}
          </div>
          <div>
            <StatusPill status={r.status} />
          </div>
          <div style={{ color: colors.textMuted, fontSize: 12 }}>
            {formatDate(r.created_at)}
          </div>
          <div
            style={{
              textAlign: 'right',
              color: colors.purple,
              fontSize: 18,
              fontWeight: 700,
            }}
          >
            ›
          </div>
        </button>
      ))}
    </div>
  )
}

function SkeletonRows() {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px solid ${colors.divider}`,
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            height: 60,
            borderBottom: i === 3 ? 'none' : `1px solid ${colors.divider}`,
            background: `linear-gradient(90deg, transparent, rgba(127,63,152,0.06), transparent)`,
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.4s linear infinite',
          }}
        />
      ))}
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
    </div>
  )
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div
      style={{
        background: colors.white,
        border: `1px dashed ${colors.gray}`,
        borderRadius: 12,
        padding: '60px 24px',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '3px',
          color: colors.purple,
          marginBottom: 12,
          textTransform: 'uppercase',
        }}
      >
        No Proposals Yet
      </div>
      <h2
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: '-0.5px',
          margin: '0 0 8px',
          color: colors.black,
        }}
      >
        Paste your demo notes to build the first one
      </h2>
      <p
        style={{
          fontSize: 14,
          color: colors.textMuted,
          maxWidth: 460,
          margin: '0 auto 20px',
          lineHeight: 1.6,
        }}
      >
        Drop in call notes, an email recap, or a Calendly invite. The portal
        extracts the prospect, company, city, and any pricing context you
        flagged — then renders a Parkify-branded PDF.
      </p>
      <button
        onClick={onNew}
        style={{
          background: colors.purple,
          color: colors.white,
          border: 'none',
          borderRadius: 10,
          padding: '12px 22px',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '1.2px',
          textTransform: 'uppercase',
        }}
      >
        + Start a proposal
      </button>
    </div>
  )
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '14px 18px',
        background: colors.warningBg,
        border: `1.5px solid ${colors.warningBorder}`,
        borderRadius: 10,
        fontSize: 13,
        color: colors.black,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ color: colors.red, fontWeight: 700 }}>⚠</span>
      Couldn't load proposals: {message}
    </div>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}
